/**
 * One time move of the old browser-only push toggle to the server.
 *
 * Before the main chat, "Chat Notifications" was stored only in this
 * browser's persisted `ui-storage` (`notifyOnChatCompletion`) and sent with
 * every request. web-backend now owns the preference
 * (`user_settings.notify_on_agent_message`, default on). A user who had
 * switched it off here must not start getting pushes, so after login: when
 * the server still reports its default (`is_default: true`) and the old
 * stored value was `false`, save `false` on the server once.
 *
 * Every step is best effort and wrapped in try/catch: storage can be blocked
 * and the endpoint can be missing (an older backend), and neither may break
 * the app.
 */

import type { QueryClient } from '@tanstack/react-query';
import { getUserPreferences, updateUserPreferences } from '@/api/users';
import { queryClient as appQueryClient, queryKeys } from '@/lib/queryClient';
import { clearLegacyNotifyOff, hasLegacyNotifyOff } from '@/lib/legacyNotifyPreference';

/**
 * Run the migration. Resolves to true when it saved `false` on the server.
 * Never throws. A failure keeps the stashed value so the next load retries.
 */
export async function migrateNotifyPreference(
  queryClient: QueryClient = appQueryClient
): Promise<boolean> {
  if (!hasLegacyNotifyOff()) return false;
  try {
    const prefs = await getUserPreferences();
    if (prefs.is_default) {
      const saved = await updateUserPreferences({ notify_on_agent_message: false });
      clearLegacyNotifyOff();
      // The Notifications toggle may have fetched the default meanwhile:
      // drop that in flight read and cache what the server now holds.
      await queryClient.cancelQueries({ queryKey: queryKeys.user.preferences });
      queryClient.setQueryData(queryKeys.user.preferences, saved);
      return true;
    }
    // The server already holds a saved choice: it wins.
    clearLegacyNotifyOff();
    return false;
  } catch {
    // Endpoint missing or offline: try again on the next load.
    return false;
  }
}
